/**
 * Collection / search grid — "Click to load more" and "Infinite scroll".
 *
 * One custom element, <collection-load-more>, drives both modes; only the
 * trigger differs (a button click vs. a sentinel entering the viewport).
 * Numbered pagination needs no JS at all and never renders this element.
 *
 * SERVER-AUTHORITATIVE BY DESIGN
 *   Liquid's {% paginate %} decides the page boundaries. This script never
 *   slices or hides products client-side — it asks the Section Rendering API
 *   for `?page=N` and appends whatever comes back. That is why the "Products
 *   per page" setting governs the first batch and every later batch alike,
 *   and why the batches stay correct under filters and sorting.
 *
 * NO COUPLING WITH THE FILTER ENGINE
 *   The element is rendered inside #ProductGridContainer, the subtree
 *   assets/facets.js replaces wholesale on every filter/sort change. The old
 *   instance therefore disconnects (observer + listeners torn down) and a new
 *   one, rendered by Liquid for the filtered result set, connects with fresh
 *   page counts. Nothing has to be wired between the two files — do not move
 *   this control outside that container.
 *
 * APPENDED CARDS NEED NO INITIALISATION
 *   Every interactive part of a product card is a custom element in this
 *   theme (<modal-opener>, <wishlist-item>, <compare-item>,
 *   <compare-grid-select>, <countdown-timer>, <product-form>, the swatch
 *   elements), so inserting the parsed nodes upgrades them automatically. The
 *   "collection:products-appended" event below exists for anything that is
 *   NOT a custom element and needs a hook later.
 *
 * See Blueprint/pagination.md.
 */
(function () {
  "use strict";

  if (!window.customElements || customElements.get("collection-load-more")) return;

  /* Start fetching this far before the sentinel is actually on screen, so a
     batch is usually in place by the time the shopper reaches the bottom. */
  var PREFETCH_MARGIN = "600px 0px";

  function readPage(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) || parsed < 1 ? fallback : parsed;
  }

  customElements.define(
    "collection-load-more",
    class extends HTMLElement {
      connectedCallback() {
        this.mode = this.dataset.mode === "infinite" ? "infinite" : "load_more";
        this.sectionId = this.dataset.sectionId;
        this.currentPage = readPage(this.dataset.currentPage, 1);
        this.totalPages = readPage(this.dataset.totalPages, 1);
        this.gridSelector = this.dataset.gridSelector || "[data-pagination-grid]";
        this.itemSelector = this.dataset.itemSelector || ".col";

        this.grid = document.querySelector(this.gridSelector);
        this.button = this.querySelector("[data-load-more-button]");
        this.sentinel = this.querySelector("[data-load-more-sentinel]");
        this.endMessage = this.querySelector("[data-load-more-end]");
        this.announcer = this.querySelector("[data-load-more-status]");
        this.isBusy = false;

        this.onClick = this.onClick.bind(this);
        this.onIntersect = this.onIntersect.bind(this);

        /* Nothing left to fetch, or the grid contract does not match the
           markup — either way, settle into the finished state instead of
           leaving a trigger that can only fail. */
        if (!this.grid || !this.dataset.nextUrl || this.currentPage >= this.totalPages) {
          this.finish();
          return;
        }

        var canObserve = this.mode === "infinite" && "IntersectionObserver" in window && this.sentinel;

        if (canObserve) {
          this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: PREFETCH_MARGIN });
          this.observer.observe(this.sentinel);
        } else if (this.button) {
          /* Infinite scroll without IntersectionObserver degrades to the
             button, which is present in both modes and only hidden by CSS —
             this class reveals it again. */
          if (this.mode === "infinite") this.classList.add("collection-load-more--no-observer");
          this.button.addEventListener("click", this.onClick);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.button) this.button.removeEventListener("click", this.onClick);
      }

      onClick(event) {
        event.preventDefault();
        this.loadNextPage();
      }

      onIntersect(entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            this.loadNextPage();
            return;
          }
        }
      }

      /* Page number aside, the request carries whatever is in the address bar
         — facets.js keeps filters and sort_by there via pushState, so loading
         always continues inside the active filter context. A stale `page`
         param (from a numbered-pagination deep link) is dropped first. */
      buildRequestUrl() {
        var params = new URLSearchParams(window.location.search);
        params.delete("page");
        params.set("section_id", this.sectionId);
        params.set("page", String(this.currentPage + 1));
        return window.location.pathname + "?" + params.toString();
      }

      loadNextPage() {
        if (this.isBusy || this.currentPage >= this.totalPages) return;

        this.isBusy = true;
        this.setBusy(true);

        var self = this;

        fetch(this.buildRequestUrl())
          .then(function (response) {
            if (!response.ok) throw new Error("HTTP " + response.status);
            return response.text();
          })
          .then(function (markup) {
            var parsed = new DOMParser().parseFromString(markup, "text/html");
            var incoming = parsed.querySelector(self.gridSelector);
            if (!incoming) throw new Error("No grid matching " + self.gridSelector + " in the response");

            var items = incoming.querySelectorAll(":scope > " + self.itemSelector);
            var fragment = document.createDocumentFragment();
            for (var i = 0; i < items.length; i++) fragment.appendChild(items[i]);

            /* One DOM write per batch — the cards upgrade themselves on
               insertion. */
            self.grid.appendChild(fragment);
            self.announce(items.length);

            document.dispatchEvent(
              new CustomEvent("collection:products-appended", {
                detail: { grid: self.grid, count: items.length, page: self.currentPage + 1 },
              })
            );

            self.currentPage += 1;
            self.dataset.currentPage = String(self.currentPage);

            if (self.currentPage >= self.totalPages) {
              self.finish();
            } else if (self.observer && self.sentinel) {
              /* On a tall viewport the sentinel can still be on screen after
                 the append, and IntersectionObserver will not re-fire on its
                 own. Re-observing forces a fresh callback so loading chains
                 until the sentinel is genuinely out of range. */
              self.observer.unobserve(self.sentinel);
              self.observer.observe(self.sentinel);
            }
          })
          .catch(function (error) {
            /* The trigger stays a real link to the next page, so a failed
               fetch costs the shopper a page load, not the products. */
            console.error("[collection-load-more]", error);
          })
          .then(function () {
            self.isBusy = false;
            self.setBusy(false);
          });
      }

      setBusy(busy) {
        this.classList.toggle("is-loading", busy);
        if (this.button) this.button.setAttribute("aria-busy", String(busy));
        if (busy) this.setAttribute("aria-busy", "true");
        else this.removeAttribute("aria-busy");
      }

      /* Appended cards are not announced by screen readers on their own, so
         say how many arrived. */
      announce(count) {
        if (!this.announcer || !count) return;
        var template = this.announcer.dataset.loadedTemplate || "";
        this.announcer.textContent = template.replace("[count]", String(count));
      }

      finish() {
        if (this.observer) this.observer.disconnect();
        if (this.button) this.button.setAttribute("hidden", "");
        if (this.sentinel) this.sentinel.setAttribute("hidden", "");
        if (this.endMessage) this.endMessage.removeAttribute("hidden");
      }
    }
  );
})();
